<div class="detail-panel-backdrop" *ngIf="entity" (click)="close()"></div>

<aside class="detail-panel" *ngIf="entity as sel">
  <header class="detail-header">
    <div class="detail-title-group">
      <div class="detail-title">{{ sel.name }}</div>
      <div class="detail-subtitle">
        {{ sel.constituentCount }} constituents &middot;
        {{ timeframeLabel || 'avg sentiment' }}
      </div>
    </div>
    <button
      type="button"
      class="detail-close"
      (click)="close()"
      aria-label="Close"
    >
      <mat-icon>close</mat-icon>
    </button>
  </header>

  <div class="detail-stats">
    <div class="stat" [ngClass]="averageClass(sel.average)">
      <div class="stat-value">{{ sel.average }}%</div>
      <div class="stat-label">Average</div>
    </div>
    <div class="stat neg">
      <div class="stat-value">
        <span *ngIf="!sel.loading; else statSkeleton">{{ sel.neg }}</span>
      </div>
      <div class="stat-label">Negative</div>
    </div>
    <div class="stat neu">
      <div class="stat-value">
        <span *ngIf="!sel.loading; else statSkeleton">{{ sel.neu }}</span>
      </div>
      <div class="stat-label">Neutral</div>
    </div>
    <div class="stat pos">
      <div class="stat-value">
        <span *ngIf="!sel.loading; else statSkeleton">{{ sel.pos }}</span>
      </div>
      <div class="stat-label">Positive</div>
    </div>
    <ng-template #statSkeleton>
      <span class="skeleton stat-skeleton"></span>
    </ng-template>
  </div>

  <section class="detail-section">
    <div class="detail-section-title">
      Sentiment distribution
      <span *ngIf="!sel.loading"
        >&middot; {{ sel.constituentCount }} companies</span
      >
    </div>

    <ng-container *ngIf="!sel.loading; else histogramSkeleton">
      <div class="histogram">
        <div
          class="histogram-bar"
          *ngFor="let b of distributionBins(sel)"
          [ngClass]="averageClass(b.center)"
          [style.height.%]="
            (b.count / maxBinCount(distributionBins(sel))) * 100
          "
          [attr.title]="b.count + ' companies'"
        ></div>
      </div>
      <div class="histogram-axis">
        <span>0%</span>
        <span>50%</span>
        <span>100%</span>
      </div>
      <div class="histogram-footer">
        <span>
          <span class="neg">{{ distributionPercent(sel, 'neg') }}%</span>
          negative
          <span class="pos">{{ distributionPercent(sel, 'pos') }}%</span>
          positive
        </span>
        <span class="stdev">&sigma; &plusmn;{{ distributionStdev(sel) }}</span>
      </div>
    </ng-container>

    <ng-template #histogramSkeleton>
      <div class="histogram histogram--skeleton">
        <span
          class="histogram-bar skeleton"
          *ngFor="let b of skeletonBars"
        ></span>
      </div>
      <div class="histogram-axis">
        <span>0%</span>
        <span>50%</span>
        <span>100%</span>
      </div>
      <div class="histogram-footer">
        <span class="skeleton skeleton-line" style="width: 45%"></span>
        <span class="skeleton skeleton-line" style="width: 12%"></span>
      </div>
    </ng-template>
  </section>

  <section class="detail-section" *ngIf="!aggregate">
    <div class="detail-section-title">Key events</div>
    <div class="key-events-legend">
      <span class="key-events-legend-item">
        <span class="key-events-legend-swatch contrib"></span>
        contribution to sentiment (&minus; drags it down)
      </span>
      <span class="key-events-legend-item">
        <span class="key-events-legend-swatch mentions"></span>
        news mentions
      </span>
    </div>
    <ng-container *ngIf="!keyEventsLoading; else keyEventsSkeleton">
      <div
        class="key-event"
        *ngFor="let e of keyEvents"
        (click)="navigateToKeyEvent(e)"
        [attr.role]="'button'"
        [attr.tabindex]="0"
      >
        <span class="key-event-name">
          <span class="key-event-label">{{ e.name }}</span>
          <mat-icon class="key-event-arrow">arrow_forward</mat-icon>
        </span>
        <span
          class="key-event-contrib"
          [ngClass]="e.contribution < 0 ? 'neg' : 'pos'"
        >
          {{ e.contribution > 0 ? '+' : '' }}{{ e.contribution }}%
        </span>
        <span class="key-event-mentions">{{ e.mentions | number }}</span>
      </div>
      <div class="detail-empty" *ngIf="keyEvents.length === 0">
        No key events.
      </div>
    </ng-container>
    <ng-template #keyEventsSkeleton>
      <div class="key-event" *ngFor="let r of skeletonRows">
        <span class="skeleton skeleton-line" style="width: 70%"></span>
      </div>
    </ng-template>
  </section>

  <ng-template #constituentRowTpl let-c>
    <div
      class="constituent-row"
      (click)="navigateToConstituent(c)"
      [attr.role]="'button'"
      [attr.tabindex]="0"
    >
      <div class="constituent-info">
        <div class="constituent-name">
          {{ c.name }}
          <mat-icon class="constituent-arrow">arrow_forward</mat-icon>
        </div>
        <div class="constituent-event" *ngIf="c.topEvent">
          <span class="constituent-dot" [ngClass]="c.bucket"></span>
          {{ c.topEvent.label }}
        </div>
      </div>
      <div class="constituent-metrics">
        <div class="constituent-score" [ngClass]="c.bucket">{{ c.score }}%</div>
        <div class="constituent-dev" [ngClass]="c.impact >= 0 ? 'pos' : 'neg'">
          {{ c.impact >= 0 ? '+' : '' }}{{ c.impact }}%
        </div>
      </div>
    </div>
  </ng-template>

  <section class="detail-section" *ngIf="sel.loading">
    <div class="detail-section-title">
      <span
        class="skeleton skeleton-line"
        style="width: 90px; height: 8px"
      ></span>
    </div>
    <div class="constituent-row skeleton-row" *ngFor="let r of skeletonRows">
      <div class="constituent-info">
        <span class="skeleton skeleton-line" style="width: 55%"></span>
        <span
          class="skeleton skeleton-line"
          style="width: 38%; height: 8px; margin-top: 7px"
        ></span>
      </div>
      <div class="constituent-metrics">
        <span
          class="skeleton skeleton-line"
          style="width: 34px; margin-left: auto"
        ></span>
      </div>
    </div>
  </section>

  <div
    class="detail-empty detail-section"
    *ngIf="!sel.loading && sel.constituents.length === 0"
  >
    No constituent data available.
  </div>

  <ng-container *ngIf="!sel.loading && sel.constituents.length > 0">
    <section class="detail-section" *ngIf="allDecliners(sel).length > 0">
      <div class="detail-section-title decliners">
        <mat-icon>arrow_downward</mat-icon>
        {{ showAllDecliners ? 'All' : 'Top 5' }} decliners
        <span class="detail-section-sub">Dragging the average down</span>
      </div>
      <ng-container
        *ngFor="let c of visibleDecliners(sel); trackBy: trackByKey"
      >
        <ng-container
          *ngTemplateOutlet="constituentRowTpl; context: { $implicit: c }"
        ></ng-container>
      </ng-container>
      <button
        type="button"
        class="detail-view-all"
        *ngIf="allDecliners(sel).length > 5"
        (click)="toggleShowAllDecliners()"
      >
        <ng-container *ngIf="!showAllDecliners">
          View all {{ allDecliners(sel).length }} decliners &rarr;
        </ng-container>
        <ng-container *ngIf="showAllDecliners">
          Show top 5 &larr;
        </ng-container>
      </button>
    </section>

    <section class="detail-section" *ngIf="allGainers(sel).length > 0">
      <div class="detail-section-title gainers">
        <mat-icon>arrow_upward</mat-icon>
        {{ showAllGainers ? 'All' : 'Top 5' }} gainers
        <span class="detail-section-sub">Holding the average up</span>
      </div>
      <ng-container *ngFor="let c of visibleGainers(sel); trackBy: trackByKey">
        <ng-container
          *ngTemplateOutlet="constituentRowTpl; context: { $implicit: c }"
        ></ng-container>
      </ng-container>
      <button
        type="button"
        class="detail-view-all"
        *ngIf="allGainers(sel).length > 5"
        (click)="toggleShowAllGainers()"
      >
        <ng-container *ngIf="!showAllGainers">
          View all {{ allGainers(sel).length }} gainers &rarr;
        </ng-container>
        <ng-container *ngIf="showAllGainers"> Show top 5 &larr; </ng-container>
      </button>
    </section>
  </ng-container>
</aside>
